import { User } from "@/types/User";
import { PrimaryButton } from "../Buttons/PrimaryButton";
import { RoleChip } from "../RoleChip/RoleChip";
import { SecondaryButton } from "../Buttons/SecondaryButton";
import { Spacing } from "../Spacing/Spacing";
import { UserIcon } from "../UserIcon/UserIcon";
import { useTranslations } from "next-intl";
import { useState } from "react";
import {
  IconCircleCheck,
  IconCoins,
  IconInfoCircle,
} from "@tabler/icons-react";

type NotificationChipProps = {
  read: boolean;
  disabledButtons?: boolean;
  dataLogId?: string;
  triggered?: Date;
  notificationChip: string | false;
  type: string;
  responsible?: User;
  /** No person behind this notification — show a neutral icon, never a monogram. */
  actorless?: boolean;
  place?: string;
  /** `place` is a localized placeholder, not a real name — style it as such. */
  placeUnresolved?: boolean;
  acceptText: string | false;
  rejectText: string | false;
  actionAccept?: () => void;
  actionReject?: () => void;
  onReadClick?: () => void;
  /**
   * W4-d: the row is a link. A click anywhere on it (outside the buttons)
   * opens the target; the caller marks the row read on the way. Present only
   * on rows that have somewhere to go.
   */
  onOpen?: () => void;
  /** W4-d: the actorless icon for this row, when the default info circle is not it. */
  icon?: React.ReactNode;
  /**
   * W4-d request rows: the subject's name as the READER may see it — resolved
   * by the backend at read time; undefined renders the anonymous sentence.
   */
  subjectName?: string;
  /** W4-d request rows: companions folded into the row's family. */
  companionCount?: number;
};

export const NotificationChip: React.FC<NotificationChipProps> = ({
  read,
  disabledButtons = false,
  dataLogId,
  triggered,
  responsible,
  actorless = false,
  place,
  placeUnresolved = false,
  notificationChip,
  type,
  acceptText,
  rejectText,
  actionAccept,
  actionReject,
  onReadClick,
  onOpen,
  icon,
  subjectName,
  companionCount,
}) => {
  const t = useTranslations("roles");
  const t2 = useTranslations("times");
  const tn = useTranslations("popup-notifications");
  const [isHovered, setIsHovered] = useState(false);

  // Returns the whole phrase, "ago" wrapper included — German puts it in front
  // ("vor 6 Tagen"), English behind ("6 Days ago"), so the two can't be
  // concatenated at the call site. The <60s case is deliberately NOT wrapped:
  // "vor Jetzt" / "Now ago" is what came out when it was.
  const getRelativeTime = (date?: Date) => {
    if (!date) return t2("now");

    const parsedDate = date instanceof Date ? date : new Date(date);

    // The gap between two instants is tz-independent — subtract the raw
    // timestamps directly. Shifting by getTimezoneOffset() (as before) added
    // the browser offset into the diff, so "vor X" was wrong by that offset and
    // broke across DST.
    const now = new Date();
    const diffMs = now.getTime() - parsedDate.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) return t2("now");

    const value =
      diffMinutes === 1
        ? `${diffMinutes} ${t2("minute")}`
        : diffMinutes < 60
          ? `${diffMinutes} ${t2("minutes")}`
          : diffHours === 1
            ? `${diffHours} ${t2("hour")}`
            : diffHours < 24
              ? `${diffHours} ${t2("hours")}`
              : diffDays === 1
                ? `${diffDays} ${t2("day")}`
                : diffDays < 365
                  ? `${diffDays} ${t2("days")}`
                  : diffYears === 1
                    ? `${diffYears} ${t2("year")}`
                    : `${diffYears} ${t2("years")}`;

    return t2("ago", { value });
  };

  return (
    <div
      typeof={type}
      data-log-id={dataLogId}
      className={`relative flex flex-row w-full pl-3 pr-6 py-4 border-b outline-quaternary notification-item ${
        !read || onOpen
          ? "cursor-pointer hover:surface-tertiary transition-colors duration-200"
          : ""
      } ${onOpen ? "focus-ring focus-visible:-outline-offset-2" : ""}
`}
      data-request-link={onOpen ? "true" : undefined}
      // Lens 6 #9: the only rows in the bell that go somewhere were
      // mouse-only. A row with a target is a link for the keyboard and the
      // screen reader too — Enter / Space follow it like a click does.
      role={onOpen ? "link" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.target !== e.currentTarget) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
      onMouseEnter={() => !read && setIsHovered(true)}
      onMouseLeave={() => !read && setIsHovered(false)}
      onClick={(e) => {
        // A row with a target opens it (the caller marks it read on the way);
        // the buttons inside own their own clicks.
        if (onOpen) {
          const target = e.target as HTMLElement;
          if (target.closest("button, a, input, [role='button']")) return;
          onOpen();
          return;
        }
        if (!read && onReadClick) onReadClick();
      }}
    >
      {/* Top right check icon - only visible on hover and for unread items */}
      {!read && isHovered && (
        <button
          className="absolute top-2 right-2 p-1"
          onClick={(e) => {
            e.stopPropagation();
            if (onReadClick) onReadClick();
          }}
        >
          <IconCircleCheck size={16} className="font-secondary" />
        </button>
      )}

      {actorless ? (
        // No responsible user — a person avatar would fall back to the
        // "DeletedUser" monogram and paint a stray "D", so show a neutral icon.
        // AI_QUOTA_* was special-cased for exactly this reason; the same is
        // true of every other system-triggered type.
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full surface-blue">
          {/* Deliberately not IconBell — the app-bar bell is selected by its
              tabler class in e2e, and a second one inside the popup would make
              that locator ambiguous. */}
          {icon !== undefined ? (
            icon
          ) : type.startsWith("AI_QUOTA") ? (
            <IconCoins size={18} className="font-blue" />
          ) : (
            <IconInfoCircle size={18} className="font-blue" />
          )}
        </div>
      ) : (
        <UserIcon
          initialArgs={responsible?.firstName?.charAt(0)}
          monogramColorArgs={responsible?.monogramColor}
          read={read}
          size="w-8 h-8"
          fontSize="body-2-medium"
          onClickAction={() => {}}
        />
      )}
      <Spacing width="w-2.5" />
      <div className="flex flex-col body-3-regular">
        <p>
          {/* One ICU message per type, so the translator sees the whole
              sentence and can put either slot anywhere — including the name
              before the actor, which the old textZero/One/Two split could not
              express. It also made half-translated and verb-doubled sentences
              constructible; they no longer are. */}
          {tn.rich(type, {
            actorName: responsible?.firstName ?? "",
            placeName: place ?? "",
            // W4-d request rows: an ICU `select` on whether the reader may see
            // the name, plus a plural for the folded companions. Unused by
            // every other message, so passing them always is harmless.
            hasName: subjectName ? "yes" : "no",
            subjectName: subjectName ?? "",
            companionCount: companionCount ?? 0,
            subject: (chunks) => (
              <span className={subjectName ? "body-3-bold" : "body-3-regular font-tertiary italic"}>
                {chunks}
              </span>
            ),
            c: (chunks) => <span className="font-secondary">{chunks}</span>,
            actor: (chunks) => <span className="body-3-bold">{chunks}</span>,
            place: (chunks) => (
              <span
                className={
                  placeUnresolved
                    ? "body-3-regular font-tertiary italic"
                    : "body-3-bold"
                }
              >
                {chunks}
              </span>
            ),
          })}
          <span className="font-tertiary whitespace-nowrap">
            {"  "} • {getRelativeTime(triggered)}
          </span>
        </p>
        {(notificationChip ||
          (actionAccept && acceptText) ||
          (actionReject && rejectText)) && (
          <div className="pt-3 flex flex-row">
            {notificationChip && (
              <RoleChip
                text={t(`${notificationChip}`)}
                color={
                  notificationChip === "OWNER"
                    ? "green"
                    : notificationChip === "ADMIN"
                      ? "red"
                      : notificationChip === "EDITOR"
                        ? "blue"
                        : "yellow"
                }
              />
            )}
            {actionAccept && acceptText && (
              <PrimaryButton
                disabled={disabledButtons}
                margin="mr-1.5"
                width="w-max"
                size="xsmall"
                onClick={actionAccept}
                text={acceptText}
              />
            )}
            {actionReject && rejectText && (
              <SecondaryButton
                disabled={disabledButtons}
                width="w-max"
                size="xsmall"
                onClick={actionReject}
                text={rejectText}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
